import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { URLParamKey } from "constants/urlParams";
import { HMO } from "constants/hmo";
import { getParamCaseInsensitive } from "utils/urlParams.util";
import { detectHmo } from "utils/hmoDetection";
import { getLeumitToken } from "services/leumitSession";
import { validateLeumitToken } from "services/leumitTokenValidation";

export type LeumitTokenValidationStatus =
  | "idle"
  | "validating"
  | "valid"
  | "invalid"
  | "error";

const resolveLeumitToken = (search: string): string | null => {
  const queryParams = new URLSearchParams(search);
  const urlToken = getParamCaseInsensitive(queryParams, URLParamKey.TOKEN);
  const trimmedUrlToken = urlToken?.trim();

  if (trimmedUrlToken) {
    return trimmedUrlToken;
  }

  const persistedToken = getLeumitToken()?.trim();
  return persistedToken || null;
};

const requiresLeumitToken = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const isReactNativeWebView = (window as any).ReactNativeWebView !== undefined;
  const isInIframe = window.parent !== window;

  return isReactNativeWebView || isInIframe;
};

const resolveInitialStatus = (
  isLeumitTenant: boolean,
  search: string,
): LeumitTokenValidationStatus => {
  if (!isLeumitTenant) {
    return "valid";
  }

  const token = resolveLeumitToken(search);

  if (!token && !requiresLeumitToken()) {
    return "valid";
  }

  return "idle";
};

const useLeumitTokenValidation = (): {
  status: LeumitTokenValidationStatus;
  retry: () => void;
} => {
  const { search } = useLocation();
  const isLeumitTenant = detectHmo() === HMO.LEUMIT;
  const [status, setStatus] = useState<LeumitTokenValidationStatus>(() =>
    resolveInitialStatus(isLeumitTenant, search),
  );
  const [retryNonce, setRetryNonce] = useState(0);
  const inFlightTokenRef = useRef<string | null>(null);
  const hasEverValidatedRef = useRef(false);

  useEffect(() => {
    if (!isLeumitTenant) {
      setStatus("valid");
      return;
    }

    if (hasEverValidatedRef.current) {
      setStatus("valid");
      return;
    }

    let cancelled = false;

    const runValidation = async (): Promise<void> => {
      const token = resolveLeumitToken(search);

      if (!token) {
        if (!cancelled) {
          inFlightTokenRef.current = null;
          setStatus(requiresLeumitToken() ? "invalid" : "valid");
        }
        return;
      }

      if (inFlightTokenRef.current === token) {
        return;
      }

      inFlightTokenRef.current = token;

      if (!cancelled) {
        setStatus("validating");
      }

      const outcome = await validateLeumitToken(token);

      if (cancelled) {
        return;
      }

      inFlightTokenRef.current = null;

      if (outcome === "valid") {
        hasEverValidatedRef.current = true;
        setStatus("valid");
        return;
      }

      if (outcome === "invalid") {
        setStatus("invalid");
        return;
      }

      setStatus("error");
    };

    void runValidation();

    return () => {
      cancelled = true;
      inFlightTokenRef.current = null;
    };
  }, [isLeumitTenant, search, retryNonce]);

  const handleRetry = useCallback((): void => {
    inFlightTokenRef.current = null;
    setRetryNonce((current) => current + 1);
  }, []);

  return {
    status,
    retry: handleRetry,
  };
};

export default useLeumitTokenValidation;
